import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";

import { toast } from "react-toastify";

import LoadableButton from "src/components/LoadableButton";
import ConfirmDialog from "src/components/ConfirmDialog";
import { userRoleSelector } from "src/store/system/selector";
import withFeatureFlag from "src/utils/withFeatureFlag";
import {
  inviteAccountant,
  getAccountantInvitation,
  revokeAccountant,
  type AccountantInvitation,
} from "src/apiService/modules/accountant";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useStyles = makeStyles((theme) => ({
  header: {
    fontWeight: theme.typography.fontWeightBold,
  },
  sub: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  infoBox: {
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    margin: theme.spacing(2, 0),
    fontSize: 12.5,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
  },
  inviteRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  inviteButton: {
    marginTop: theme.spacing(1),
    height: 40,
    whiteSpace: "nowrap",
  },
  listLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 11,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    margin: theme.spacing(2, 0, 1),
  },
  row: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  email: {
    fontWeight: theme.typography.fontWeightBold,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: theme.typography.fontWeightBold,
    padding: "2px 8px",
    borderRadius: 12,
  },
  pillActive: {
    background: "#e8f3ec",
    color: "#1d6e3d",
  },
  pillPending: {
    background: "#faeeda",
    color: "#854f0b",
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#1d6e3d",
  },
  metaText: {
    fontSize: 11,
    color: theme.palette.text.hint,
  },
  removeButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    whiteSpace: "nowrap",
  },
}));

const EXPLAINER =
  "Your accountant will be able to view, edit, delete, export, and reconcile " +
  "your data. They can't change your subscription, see your billing info, " +
  "add/delete integrations, or sync data from Mercari/Poshmark/Whatnot " +
  "integrations.";

function formatDate(epochMillis: number): string {
  return new Date(epochMillis).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface AccountantRowProps {
  invitation: AccountantInvitation;
  disabled: boolean;
  onRemove: () => void;
}

function AccountantRow({ invitation, disabled, onRemove }: AccountantRowProps) {
  const classes = useStyles();
  const isActive = invitation.status === "active";
  return (
    <>
      <Typography className={classes.listLabel}>
        {isActive ? "Active" : "Pending"}
      </Typography>
      <Box className={classes.row}>
        <Box minWidth={0}>
          <Typography className={classes.email}>
            {invitation.accountant}
          </Typography>
          <Box className={classes.meta}>
            {isActive ? (
              <span className={clsx(classes.pill, classes.pillActive)}>
                <span className={classes.dot} />
                Active
              </span>
            ) : (
              <span className={clsx(classes.pill, classes.pillPending)}>
                Pending
              </span>
            )}
            <span className={classes.metaText}>
              {isActive
                ? `Added ${formatDate(
                    invitation.acceptedAt ?? invitation.createdAt
                  )}`
                : `Invitation sent ${formatDate(invitation.createdAt)}`}
            </span>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          className={classes.removeButton}
          disabled={disabled}
          onClick={onRemove}
        >
          Remove
        </Button>
      </Box>
    </>
  );
}

interface AccountantAccessProps {
  className?: string;
}

function AccountantAccess({ className }: AccountantAccessProps) {
  const classes = useStyles();
  const userRole = useSelector(userRoleSelector);
  const isViewer = userRole === "viewer";
  // Accountants can't invite/remove accountants (permissions matrix), and the
  // fetch below would always fail for them — hide the section entirely.
  const isAccountant = userRole === "accountant";

  const [invitation, setInvitation] = useState<AccountantInvitation | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const current = await getAccountantInvitation();
      setInvitation(current);
    } catch (e) {
      toast.error(`Couldn't load accountant access: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAccountant) return;
    refresh();
  }, [isAccountant, refresh]);

  const formik = useFormik({
    initialValues: { email: "" },
    validate: (values) => {
      const errors: { email?: string } = {};
      const email = values.email.trim();
      if (!email) {
        errors.email = "Please enter an email address.";
      } else if (!EMAIL_RE.test(email)) {
        errors.email = "Please enter a valid email address.";
      }
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const result = await inviteAccountant(values.email.trim());
        setInvitation(result);
        resetForm();
        toast.success("Invitation sent.");
      } catch (e) {
        formik.setFieldError("email", (e as Error).message);
        toast.error(`Couldn't send invite: ${(e as Error).message}`);
      }
    },
  });

  const handleRemove = useCallback(async () => {
    try {
      await revokeAccountant();
      setInvitation(null);
      setConfirmRemoveOpen(false);
      toast.success("Accountant access removed.");
    } catch (e) {
      toast.error(`Couldn't remove access: ${(e as Error).message}`);
    }
  }, []);

  if (isAccountant) return null;

  return (
    <Grid item xs={12} className={className}>
      <Typography className={classes.header}>Accountant Access</Typography>
      <Typography variant="body2" className={classes.sub}>
        Give your accountant or bookkeeper their own login to your books.
      </Typography>

      <div className={classes.infoBox}>{EXPLAINER}</div>

      <form onSubmit={formik.handleSubmit}>
        <Box className={classes.inviteRow}>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="email"
            id="accountant-email-input"
            name="email"
            label="Accountant's email"
            placeholder="name@firm.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && !!formik.errors.email}
            helperText={formik.touched.email ? formik.errors.email : undefined}
            disabled={formik.isSubmitting || isViewer}
            InputProps={{ readOnly: isViewer }}
          />
          <LoadableButton
            type="submit"
            color="primary"
            variant="contained"
            className={classes.inviteButton}
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting || !formik.isValid || isViewer}
          >
            Send invite
          </LoadableButton>
        </Box>
      </form>

      {loading ? (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      ) : invitation ? (
        <AccountantRow
          invitation={invitation}
          disabled={isViewer}
          onRemove={() => setConfirmRemoveOpen(true)}
        />
      ) : null}

      <ConfirmDialog
        open={confirmRemoveOpen}
        title="Remove accountant access"
        text={
          invitation
            ? `Remove access for ${invitation.accountant}? They'll be signed out immediately and won't be able to access your account.`
            : ""
        }
        confirm="Remove"
        onCancel={() => setConfirmRemoveOpen(false)}
        onConfirm={handleRemove}
      />
    </Grid>
  );
}

export default withFeatureFlag<AccountantAccessProps>("accountant")(
  AccountantAccess
);
